import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiControlDirective } from '@template-ui/directives';
import type { FormIssues } from '@template-ui/helpers/generated-form.helpers';
import { WorkflowReasonCodeComponent } from '../workflow-reason-code/workflow-reason-code.component';
import type {
  WorkflowReasonCapturePresentation,
  WorkflowReasonCaptureValue,
} from '@app/shared/workflow-commands/workflow-reason-capture';

let reasonCaptureSequence = 0;

/** Shared local input surface. The owning command resolves catalogs and commits the command. */
@Component({
  selector: 'app-workflow-reason-capture',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiControlDirective,
    WorkflowReasonCodeComponent,
  ],
  template: `<div class="reason-capture">
    @if (presentation.contract.codeField) {
      <app-workflow-reason-code
        [inputId]="inputId + '-code'"
        [label]="
          (presentation.codeLabel || 'Reason code') +
          (presentation.contract.codeRequired ? ' *' : '')
        "
        [choices]="presentation.choices"
        [value]="value.code"
        [disabled]="disabled"
        [error]="presentation.unavailable || ''"
        [required]="presentation.contract.codeRequired"
        [validationError]="issues[presentation.contract.codeField!] || ''"
        (valueChange)="codeChanged($event)"
      />
    }
    @if (presentation.contract.textField) {
      <div class="reason-text">
        <label [for]="inputId + '-text'"
          >{{ presentation.textLabel || 'Reason'
          }}{{ presentation.contract.textRequired ? ' *' : '' }}</label
        >
        <textarea
          uiControl
          rows="3"
          [id]="inputId + '-text'"
          [name]="inputId + '-text'"
          [ngModel]="value.text"
          (ngModelChange)="textChanged($event)"
          [ngModelOptions]="standalone"
          [attr.maxlength]="presentation.contract.textMaxLength"
          [disabled]="disabled || !!presentation.unavailable"
          [attr.aria-required]="presentation.contract.textRequired"
          [attr.aria-invalid]="
            issues[presentation.contract.textField!] ? 'true' : null
          "
          [attr.aria-describedby]="
            issues[presentation.contract.textField!]
              ? inputId + '-text-error'
              : null
          "
        ></textarea>
        <p
          *ngIf="issues[presentation.contract.textField!]"
          [id]="inputId + '-text-error'"
          class="issue"
          role="alert"
        >
          {{ issues[presentation.contract.textField!] }}
        </p>
      </div>
    }
    <p *ngIf="issues['reason']" class="issue" role="alert">
      {{ issues['reason'] }}
    </p>
    <p
      *ngIf="presentation.unavailable && !presentation.contract.codeField"
      class="issue"
      role="alert"
    >
      {{ presentation.unavailable }}
    </p>
  </div>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .reason-capture,
      .reason-text {
        display: grid;
        gap: 0.45rem;
      }
      textarea {
        width: 100%;
        min-width: 0;
        resize: vertical;
      }
      p {
        margin: 0;
      }
      .issue {
        color: var(--custom-text-color-error);
      }
    `,
  ],
})
export class WorkflowReasonCaptureComponent {
  @Input({ required: true }) presentation!: WorkflowReasonCapturePresentation;
  @Input() value: WorkflowReasonCaptureValue = { code: '', text: '' };
  @Input() issues: FormIssues = {};
  @Input() disabled = false;
  @Input() inputId = `workflow-reason-capture-${++reasonCaptureSequence}`;
  @Output() valueChange = new EventEmitter<WorkflowReasonCaptureValue>();
  readonly standalone = { standalone: true };

  codeChanged(code: string): void {
    if (
      this.disabled ||
      this.presentation.unavailable ||
      (code !== '' &&
        !this.presentation.choices.some((choice) => choice.code === code))
    )
      return;
    this.valueChange.emit({ ...this.value, code });
  }
  textChanged(text: string): void {
    if (!this.disabled && !this.presentation.unavailable)
      this.valueChange.emit({ ...this.value, text });
  }
}
